import { DomainFault } from '../../contracts';
import type { Entity } from '../../types';
import { durationOf, sceneOf, validateEvidence } from '../recording';
import type {
  AnalysisResultVersion,
  CandidateSuggestion,
  ConfusionMatrix,
  FrozenRecording,
  QualityPrediction,
  RuleSnapshot,
  TemplateSettings,
  TemplateVersion,
} from './types';
import { allScenes, outputLabels } from './types';
export function stableUuid(value: string) {
  const values = [2166136261, 2246822519, 3266489917, 668265263];
  for (const ch of value)
    for (let i = 0; i < values.length; i++)
      values[i] =
        Math.imul(values[i] ^ ch.charCodeAt(0), 16777619 + i * 2) >>> 0;
  const hex = values.map((v) => v.toString(16).padStart(8, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-5${hex.slice(13, 16)}-a${hex.slice(17, 20)}-${hex.slice(20)}`;
}
export function fingerprint(value: unknown) {
  return stableUuid(JSON.stringify(value));
}
export function freezeRecording(row: Entity): FrozenRecording {
  const version = row.versions?.at(-1);
  if (
    row.kind !== 'recording' ||
    !version?.segments.length ||
    !version.segments.every(
      (s) =>
        s.text.trim() &&
        Number.isFinite(s.startMs) &&
        Number.isFinite(s.endMs) &&
        s.startMs >= 0 &&
        s.endMs > s.startMs,
    )
  )
    throw new DomainFault('VALIDATION', '录音缺少有效转写或片段边界');
  return {
    uuid: row.uuid,
    revision: row.revision,
    title: row.title,
    campus: row.campus,
    memberUuid: row.memberUuid,
    transcriptVersionUuid: version.uuid,
    segments: structuredClone(version.segments),
    durationMs: durationOf(row),
    scene: sceneOf(row),
  };
}
export function validateSettings(value: TemplateSettings) {
  if (
    !allScenes.includes(value.scene) ||
    !Array.isArray(value.outputs) ||
    !value.outputs.length ||
    value.outputs.some((k) => !Object.hasOwn(outputLabels, k)) ||
    new Set(value.outputs).size !== value.outputs.length ||
    !Array.isArray(value.ruleUuids) ||
    !value.ruleUuids.length ||
    !Number.isInteger(value.minMatches) ||
    value.minMatches < 1 ||
    value.minMatches > 20 ||
    typeof value.prompt !== 'string' ||
    value.prompt.length > 10000
  )
    throw new DomainFault(
      'VALIDATION',
      '请填写有效场景、输出类别、规则组合和1–20的命中次数',
    );
}
export function analyzeFrozen(
  input: FrozenRecording,
  template: TemplateVersion,
  rules: RuleSnapshot[],
  at: string,
  version = 1,
): AnalysisResultVersion {
  const suggestions: CandidateSuggestion[] = [];
  const contributions: AnalysisResultVersion['contributions'] = [];
  const applicable =
    template.settings.scene === '全部场景' ||
    template.settings.scene === input.scene;
  for (const rule of rules) {
    const matches = applicable
      ? input.segments.filter((s) => s.text.includes(rule.keyword))
      : [];
    const included = matches.filter(
      (s) => !rule.exclusion || !s.text.includes(rule.exclusion),
    );
    contributions.push({
      ruleUuid: rule.uuid,
      matched: included.length,
      excluded: matches.length - included.length,
    });
    if (included.length < template.settings.minMatches) continue;
    for (const segment of included)
      for (const category of template.settings.outputs) {
        const evidence = {
          recordingUuid: input.uuid,
          transcriptVersionUuid: input.transcriptVersionUuid,
          startMs: segment.startMs,
          endMs: segment.endMs,
          quote: segment.text,
        };
        suggestions.push({
          uuid: stableUuid(
            JSON.stringify([
              input.uuid,
              input.transcriptVersionUuid,
              template.uuid,
              rule.uuid,
              rule.revision,
              segment.uuid,
              category,
            ]),
          ),
          category,
          title: `${outputLabels[category]} · ${rule.title}`,
          observation: `冻结转写片段出现“${rule.keyword}”，满足模板命中条件；这是一条待人工核实的候选。`,
          rule: structuredClone(rule),
          evidence,
          segmentUuids: [segment.uuid],
          missingInformation: [
            '说话人身份及语境需人工核实',
            ...(category === 'service'
              ? ['履行结果、责任人与截止时间尚未确认']
              : ['单段证据不代表完整事实或绩效结论']),
          ],
        });
      }
  }
  return {
    uuid: stableUuid(
      `${input.uuid}:${input.transcriptVersionUuid}:${template.uuid}:${fingerprint(rules)}:${version}`,
    ),
    version,
    createdAt: at,
    input: structuredClone(input),
    templateVersionUuid: template.uuid,
    processing: structuredClone(template.processing),
    rules: structuredClone(rules),
    suggestions,
    contributions,
    limitations: [
      ...(!applicable ? ['录音场景与模板不符，本次不产生候选'] : []),
      ...(!suggestions.length
        ? ['未命中规则不代表事实不存在，请检查规则覆盖与转写质量']
        : []),
      '确定性关键词模拟，不使用真实模型；自由提示词仅保存，不执行语义推断',
    ],
  };
}
export function matrix(
  rows: QualityPrediction[],
  missesReviewed: boolean,
): ConfusionMatrix {
  let tp = 0,
    fp = 0,
    fn = 0,
    tn = 0,
    excluded = 0;
  for (const s of rows) {
    if (!['positive', 'negative'].includes(s.truth)) {
      excluded++;
      continue;
    }
    if (s.truth === 'positive') {
      if (s.predicted) tp++;
      else fn++;
    } else if (s.predicted) fp++;
    else tn++;
  }
  const total = tp + fp + fn + tn;
  return {
    tp,
    fp,
    fn,
    tn,
    excluded,
    total,
    precision: tp + fp ? tp / (tp + fp) : null,
    recall: missesReviewed && tp + fn ? tp / (tp + fn) : null,
    accuracy: total ? (tp + tn) / total : null,
  };
}
export function predictionFor(
  sample: {
    input: FrozenRecording;
    evidence: { startMs: number; endMs: number };
  },
  template: TemplateVersion,
) {
  return analyzeFrozen(
    sample.input,
    template,
    template.rules,
    template.createdAt,
  ).suggestions.some(
    (s) =>
      s.evidence.startMs < sample.evidence.endMs &&
      s.evidence.endMs > sample.evidence.startMs,
  );
}
export function validateCurrentEvidence(
  row: Entity,
  input: FrozenRecording,
  startMs: number,
  endMs: number,
) {
  return validateEvidence(row, {
    recordingUuid: row.uuid,
    transcriptVersionUuid: input.transcriptVersionUuid,
    startMs,
    endMs,
    quote: '',
  });
}
